import { Box, Container, Typography, Button, Tabs, Tab } from "@mui/material";
import {
  Add as AddIcon,
  Article as ContentIcon,
  Psychology as PromptIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Router from "@/constants/router";
import useSuperChatContentList from "./useSuperChatContentList";
import useSuperChatPromptList from "./useSuperChatPromptList";
import SuperChatContent from "@/templates/SuperChatContent";
import SuperChatPrompt from "@/templates/SuperChatPrompt";
import { FloatingAiChat } from "@/organism/FloatingAiChat";

/**
 * QNA 컨텐츠 관리 목록 페이지
 *
 * @description 도장 목록 페이지를 참고하여 Atomic Design 패턴으로 구현
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-16
 */
export const List = () => {
  const navigate = useNavigate();

  const {
    list,
    isLoading,
    page,
    count,
    filters,
    rowsPerPage,
    handleSearch,
    handleFilter,
    handleEdit,
    handlePageChange,
    handleRowsPerPageChange,
  } = useSuperChatContentList();

  const {
    list: promptList,
    isLoading: promptLoading,
    page: promptPage,
    count: promptCount,
    rowsPerPage: promptRowsPerPage,
    handlePageChange: handlePromptPageChange,
    handleRowsPerPageChange: handlePromptRowsPerPageChange,
    handleView: handlePromptView,
    refetch: handlePromptRefresh,
  } = useSuperChatPromptList();

  // 탭 상태 관리
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleCreateContent = () => {
    navigate(Router.SuperChatContentCreateContent);
  };

  const handleCreatePrompt = () => {
    navigate(Router.SuperChatContentCreatePrompt);
  };

  return (
    <Container maxWidth={"xl"}>
      {/* 타이틀 영역 */}
      <Box mb={2}>
        <Typography
          component={"h1"}
          variant="h5"
          fontWeight={"bold"}
          gutterBottom
        >
          QNA 컨텐츠 관리
        </Typography>
      </Box>

      {/* 생성 버튼 영역 */}
      <Box display={"flex"} justifyContent={"flex-end"} mb={2}>
        <Button
          variant={"contained"}
          disableElevation
          startIcon={<AddIcon />}
          onClick={currentTab === 0 ? handleCreateContent : handleCreatePrompt}
        >
          {currentTab === 0 ? "컨텐츠 생성" : "프롬프트 생성"}
        </Button>
      </Box>

      {/* 탭 네비게이션 */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="QNA 컨텐츠 관리 탭"
        >
          <Tab
            icon={<ContentIcon />}
            iconPosition="start"
            label="컨텐츠 관리"
            sx={{ minHeight: 48 }}
          />
          <Tab
            icon={<PromptIcon />}
            iconPosition="start"
            label="프롬프트 관리"
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      </Box>

      {/* 탭 컨텐츠 */}
      {currentTab === 0 && (
        <SuperChatContent.List
          page={page}
          count={count}
          list={list}
          isLoading={isLoading}
          filters={filters}
          rowsPerPage={rowsPerPage}
          onSearch={handleSearch}
          onFilter={handleFilter}
          onEdit={handleEdit}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      )}

      {currentTab === 1 && (
        <SuperChatPrompt.List
          list={promptList}
          isLoading={promptLoading}
          page={promptPage}
          count={promptCount}
          rowsPerPage={promptRowsPerPage}
          onPageChange={handlePromptPageChange}
          onRowsPerPageChange={handlePromptRowsPerPageChange}
          onView={handlePromptView}
          onRefresh={handlePromptRefresh}
        />
      )}

      {/* 🔥 Floating AI Chat 컴포넌트 */}
      <FloatingAiChat />
    </Container>
  );
};
